// Adapted from davinci-elections apps/web/src/theme/colors.ts @ 6e23f58
// Default accent (Vocdoni deep green) when PRIMARY_COLOR is not configured. The `brand` scale is
// generated from the configured color at system creation (see system.ts), so it
// is deliberately absent from this static map.
export const DEFAULT_PRIMARY_COLOR = '#296948'

export const colors = {
  // Neutral near-black scale used for dark-mode surfaces (page, cards, menus)
  // and the default black accent. Kept separate from `brand` on purpose: a
  // configured PRIMARY_COLOR must recolor the accent, never tint the surfaces.
  ink: {
    // comments refer to (unused) button styles
    50: { value: '#e5e5e5' }, // ghost hover (light)
    100: { value: '#cccccc' }, // hover (light)
    200: { value: '#fafafa' }, // outline hover / ghost active (light)
    300: { value: '#bbbbbb' }, // outline active (light)
    500: { value: '#000000' }, // base solid
    550: { value: '#262626' }, // custom (auth bg)
    600: { value: '#353535' }, // solid hover
    650: { value: '#0a0a0a' }, // custom
    700: { value: '#2e2e2e' }, // solid active
    800: { value: '#3f3f3f' }, // link active (dark)
  },

  // The surface ramp (R113, contrast pass): one cool grey with a faint lean towards the Vocdoni green, so the layered
  // surfaces read as one family next to the accent. Only the semantic tokens (semantic.ts) read it: the app background
  // (`bg.canvas`), the chrome (`bg.chrome`: sidebar, top bar, bottom tab bar, footer), the content surfaces (`bg`,
  // `bg.panel`), inset areas (`bg.subtle`), hover fills (`bg.muted`) and the borders. Static on purpose: a configured
  // PRIMARY_COLOR recolours the accent, never these surfaces.
  neutral: {
    25: { value: '#f7f9f8' }, // inset area inside a light surface (table header, nested box)
    50: { value: '#f2f4f3' }, // light app background
    100: { value: '#e2e8e4' }, // light chrome
    150: { value: '#d6ded9' }, // hover on the light chrome
    200: { value: '#d3dad6' }, // light structural border (card edges, dividers)
    250: { value: '#edf0ee' }, // light hover fill on a surface
    300: { value: '#b9c2bd' }, // light chrome edge, emphasized border
    500: { value: '#737a76' }, // light outline of an interactive control (at least 3:1 on every light surface)
    550: { value: '#838a86' }, // dark outline of an interactive control (at least 3:1 on every dark surface)
    600: { value: '#5c635f' }, // light subtle text
    640: { value: '#a0a8a4' }, // dark subtle text
    660: { value: '#b3bab6' }, // dark muted text (4.5:1 on the dark panel and hover fills)
    700: { value: '#4f5853' }, // dark chrome edge, dark emphasized border
    750: { value: '#3f4642' }, // dark structural border
    800: { value: '#3d4440' }, // dark hover fill on a card or panel
    850: { value: '#363c38' }, // dark panel (menus, dialogs) and the active menu tile on the chrome
    875: { value: '#2c322e' }, // hover on the dark chrome
    900: { value: '#2c312e' }, // dark content surface (cards)
    925: { value: '#202523' }, // dark chrome (1.27:1 on the dark page)
    950: { value: '#121413' }, // dark inset area and form field
    990: { value: '#0a0a0a' }, // dark app background (vocdoni-app's dark page: the deep accent fill reaches 3:1 on it)
  },

  // Partial override of chakra's default gray scale — only the shades that
  // differ from the defaults; the rest (200, 300, 600-950) merge through.
  gray: {
    50: { value: '#fcfcfc' },
    100: { value: 'whitesmoke' },
    400: { value: '#b2b2b2' },
    500: { value: '#737373' },
  },

  // Accents for the per-vertical auth showcase (see src/components/Auth/vertical).
  // 900 is the panel surface: a deep, desaturated tone, dark enough to carry white type at
  // roughly 13:1. 200/300 are text on it, 500 and 700 are kept for callers that want a
  // brighter step of the same hue.
  verticals: {
    generic: {
      200: { value: '#ddd6fe' },
      300: { value: '#c4b5fd' },
      500: { value: '#8b5cf6' },
      700: { value: '#4c1d95' },
      900: { value: '#251a42' },
    },
    professional: {
      200: { value: '#bae6fd' },
      300: { value: '#7dd3fc' },
      500: { value: '#0ea5e9' },
      700: { value: '#0c4a6e' },
      900: { value: '#0c2e3e' },
    },
  },

  dashboardMenu: {
    light: { value: '#fbfbfb' },
    dark: { value: '#18181b' },
  },

  whatsapp: {
    500: { value: '#25D366' },
    600: { value: '#20BA5A' },
  },

  // Translucent so it works over both color modes
  separator: { value: 'rgba(135, 140, 189, 0.3)' },
}

const n = colors.neutral

/**
 * The layered surfaces of each colour mode (R113), as hex: what `bg.canvas`, `bg.chrome`, `bg` (content surface),
 * `bg.panel` and `bg.subtle` paint (see semantic.ts). The palette generator keeps the accent readable on every one of
 * them and the contrast tests measure text, borders and controls against each.
 */
export const SURFACES = {
  light: {
    canvas: n[50].value,
    chrome: n[100].value,
    surface: '#ffffff',
    panel: '#ffffff',
    subtle: n[25].value,
  },
  dark: {
    canvas: n[990].value,
    chrome: n[925].value,
    surface: n[900].value,
    panel: n[850].value,
    subtle: n[950].value,
  },
} as const

export type SurfaceName = keyof (typeof SURFACES)['light']

// The backgrounds a generated brand palette must stay readable against: the app background of each colour mode (an
// accent fill reaches 3:1 on it) and every surface (an accent edge, a focus ring and accent text reach their minimum on
// each). Defined here, next to the ramp it reads from, so the palette generator, the theme and the tests all measure
// contrast against the backgrounds the app actually paints.
export const PAGE_BACKGROUNDS = { light: SURFACES.light.canvas, dark: SURFACES.dark.canvas }
export const SURFACE_BACKGROUNDS = {
  light: Object.values(SURFACES.light) as string[],
  dark: Object.values(SURFACES.dark) as string[],
}
